import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { villageInfo, demographics, heroes } from '../data/mock';
import { NANAKSHAHI_CALENDAR_2026, getUpcomingFestivals, getCurrentDesiMonth, DESI_MONTHS } from '../data/nanakshahiCalendar';
import { getNews, getEvents, getPattis, getHomepageTributes, createTribute, uploadImage } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { 
  Users, Home as HomeIcon, MapPin, BookOpen, ArrowRight, 
  Calendar, Newspaper, Award, ChevronRight, X, ZoomIn,
  Image, MessageSquareText, Tractor, Store, Info, Sun, Moon, Star,
  Heart, Cake, Gift, Flower2, Plus, Upload, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const HomePage = () => {
  const { t, getText, language } = useLanguage();
  const [newsItems, setNewsItems] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [pattis, setPattis] = useState([]);
  const [selectedHero, setSelectedHero] = useState(null);
  const [todayDesi, setTodayDesi] = useState(null);
  const [upcomingFestivals, setUpcomingFestivals] = useState([]);
  
  // Tributes
  const [tributes, setTributes] = useState({ shradhanjali: [], wishes: [] });
  
  // Submission forms
  const [shradhanjaliOpen, setShradhanjaliOpen] = useState(false);
  const [wishesOpen, setWishesOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [newTribute, setNewTribute] = useState({
    type: 'antim_ardas', name: '', name_pa: '', relation: '', relation_pa: '',
    message: '', message_pa: '', event_date: '', image_url: '',
    submitted_by: '', submitted_by_phone: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsData, eventData, pattiData, tributeData] = await Promise.all([
          getNews(3),
          getEvents(),
          getPattis(),
          getHomepageTributes()
        ]);
        setNewsItems(newsData);
        setEventsData(eventData.slice(0, 3));
        setPattis(pattiData);
        setTributes(tributeData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();

    // Get today's desi date and upcoming festivals
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setTodayDesi(NANAKSHAHI_CALENDAR_2026[dateStr]);
    setUpcomingFestivals(getUpcomingFestivals(14));
  }, []);

  const currentDesiMonth = getCurrentDesiMonth();

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(language === 'pa' ? 'ਫ਼ਾਈਲ 2MB ਤੋਂ ਛੋਟੀ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ' : 'File must be smaller than 2MB');
        return;
      }
      try {
        const result = await uploadImage(file);
        setNewTribute({...newTribute, image_url: result.image_url});
        setImagePreview(result.image_url);
      } catch (error) {
        toast.error(language === 'pa' ? 'ਤਸਵੀਰ ਅੱਪਲੋਡ ਨਹੀਂ ਹੋਈ' : 'Failed to upload image');
      }
    }
  };

  // Handle tribute submission
  const handleTributeSubmit = async (e) => {
    e.preventDefault();
    if (!newTribute.name || !newTribute.event_date || !newTribute.submitted_by || !newTribute.submitted_by_phone) {
      toast.error(language === 'pa' ? 'ਲੋੜੀਂਦੇ ਖੇਤਰ ਭਰੋ' : 'Please fill required fields');
      return;
    }
    setSubmitting(true);
    try {
      await createTribute({
        ...newTribute,
        name_pa: newTribute.name_pa || newTribute.name,
        event_date: new Date(newTribute.event_date).toISOString()
      }, false);
      setSubmitted(true);
      setTimeout(() => {
        setShradhanjaliOpen(false);
        setWishesOpen(false);
        setSubmitted(false);
        setImagePreview(null);
        setNewTribute({
          type: 'antim_ardas', name: '', name_pa: '', relation: '', relation_pa: '',
          message: '', message_pa: '', event_date: '', image_url: '',
          submitted_by: '', submitted_by_phone: ''
        });
      }, 2000);
    } catch (error) {
      toast.error(language === 'pa' ? 'ਗਲਤੀ ਹੋਈ' : 'Error submitting');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { icon: Users, label: t('population'), value: demographics.totalPopulation.toLocaleString(), color: 'from-blue-500 to-cyan-500' },
    { icon: HomeIcon, label: t('households'), value: demographics.households, color: 'from-emerald-500 to-teal-500' },
    { icon: MapPin, label: t('area'), value: villageInfo.area, color: 'from-amber-500 to-orange-500' },
    { icon: BookOpen, label: t('literacyRate'), value: `${demographics.literacyRate}%`, color: 'from-purple-500 to-pink-500' },
  ];

  const getNewsText = (item, field) => {
    return language === 'pa' ? item[`${field}_pa`] : item[`${field}_en`];
  };

  const getEventText = (item, field) => {
    return language === 'pa' ? item[`${field}_pa`] : item[`${field}_en`];
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23f59e0b%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-60"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" />
              {getText(villageInfo.district)}, {getText(villageInfo.state)}
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4">
              {t('welcomeTo')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                {getText(villageInfo.name)}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              {getText(villageInfo.tagline)}
            </p>
            
            <p className="text-gray-500 max-w-2xl mx-auto mb-10">
              {t('discoverRich')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-200 px-8">
                  {t('about')} {getText(villageInfo.name)}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/directory">
                <Button size="lg" variant="outline" className="border-amber-300 hover:bg-amber-50 px-8">
                  {t('directory')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Quick Navigation Tabs */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {[
              { to: '/about', icon: Info, label: { en: 'About', pa: 'ਜਾਣਕਾਰੀ' } },
              { to: '/directory', icon: Users, label: { en: 'Directory', pa: 'ਡਾਇਰੈਕਟਰੀ' } },
              { to: '/services', icon: Store, label: { en: 'Services', pa: 'ਸੇਵਾਵਾਂ' } },
              { to: '/news', icon: Newspaper, label: { en: 'News', pa: 'ਖ਼ਬਰਾਂ' } },
              { to: '/events', icon: Calendar, label: { en: 'Events', pa: 'ਸਮਾਗਮ' } },
              { to: '/gallery', icon: Image, label: { en: 'Gallery', pa: 'ਗੈਲਰੀ' } },
              { to: '/agriculture', icon: Tractor, label: { en: 'Agriculture', pa: 'ਖੇਤੀਬਾੜੀ' } },
              { to: '/grievance', icon: MessageSquareText, label: { en: 'Grievance', pa: 'ਸ਼ਿਕਾਇਤ' } },
            ].map((item) => (
              <Link key={item.to} to={item.to}>
                <Button 
                  variant="outline" 
                  className="border-amber-200 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-700 transition-all duration-200 gap-2"
                >
                  <item.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{language === 'pa' ? item.label.pa : item.label.en}</span>
                  <span className="sm:hidden">{language === 'pa' ? item.label.pa : item.label.en}</span>
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Desi Calendar Widget */}
      <section className="py-8 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Today's Date */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Sun className="h-5 w-5" />
                    <h3 className="font-bold">
                      {language === 'pa' ? 'ਅੱਜ ਦੀ ਤਾਰੀਖ਼' : "Today's Date"}
                    </h3>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="text-center bg-amber-100 rounded-xl px-4 py-3">
                      <p className="text-3xl font-bold text-amber-700">{new Date().getDate()}</p>
                      <p className="text-xs text-amber-600">{new Date().toLocaleDateString('en', { month: 'short' })}</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-gray-900">
                        {currentDesiMonth} {DESI_MONTHS[currentDesiMonth]?.en && `(${DESI_MONTHS[currentDesiMonth].en})`}
                      </p>
                      {todayDesi && (
                        <>
                          <p className="text-amber-600 font-medium">{todayDesi.desi}</p>
                          <p className="text-sm text-gray-500">{todayDesi.tithi}</p>
                          {todayDesi.festival && (
                            <div className="mt-2 inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-sm">
                              <Star className="h-3 w-3" />
                              {language === 'pa' ? todayDesi.festival : todayDesi.festival_en}
                            </div>
                          )}
                        </>
                      )}
                      {!todayDesi && (
                        <p className="text-sm text-gray-500">
                          {language === 'pa' ? 'ਨਾਨਕਸ਼ਾਹੀ ਕੈਲੰਡਰ 2026' : 'Nanakshahi Calendar 2026'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Festivals */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Moon className="h-5 w-5" />
                    <h3 className="font-bold">
                      {language === 'pa' ? 'ਆਉਣ ਵਾਲੇ ਪੁਰਬ' : 'Upcoming Festivals'}
                    </h3>
                  </div>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto">
                  {upcomingFestivals.length > 0 ? (
                    <div className="space-y-3">
                      {upcomingFestivals.slice(0, 5).map((fest, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="text-center bg-purple-100 rounded-lg px-2 py-1 min-w-[50px]">
                            <p className="text-xs text-purple-600">{fest.displayDate}</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {language === 'pa' ? fest.festival : fest.festival_en}
                            </p>
                            <p className="text-xs text-gray-500">{fest.desi}</p>
                          </div>
                          {fest.sangrand && (
                            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded">
                              {language === 'pa' ? 'ਸੰਗਰਾਂਦ' : 'Sangrand'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {language === 'pa' ? 'ਕੋਈ ਪੁਰਬ ਨਹੀਂ' : 'No upcoming festivals'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Shradhanjali & Wishes Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Shradhanjali Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                    <Flower2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {language === 'pa' ? 'ਸ਼ਰਧਾਂਜਲੀ' : 'Shradhanjali'}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {language === 'pa' ? 'ਅੰਤਿਮ ਅਰਦਾਸ ਅਤੇ ਸਸਕਾਰ' : 'Antim Ardas & Cremation'}
                    </p>
                  </div>
                </div>
                <Dialog open={shradhanjaliOpen} onOpenChange={setShradhanjaliOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-gray-800 hover:bg-gray-900">
                      <Plus className="h-4 w-4 mr-1" />
                      {language === 'pa' ? 'ਸ਼ਾਮਲ ਕਰੋ' : 'Add'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    {submitted ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                          <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {language === 'pa' ? 'ਸਫਲਤਾਪੂਰਵਕ ਜਮ੍ਹਾਂ!' : 'Submitted Successfully!'}
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {language === 'pa' ? 'ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਦਿਖਾਈ ਦੇਵੇਗਾ' : 'Will appear after admin approval'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <DialogHeader>
                          <DialogTitle>{language === 'pa' ? 'ਸ਼ਰਧਾਂਜਲੀ ਸ਼ਾਮਲ ਕਰੋ' : 'Add Shradhanjali'}</DialogTitle>
                          <DialogDescription>
                            {language === 'pa' ? 'ਅੰਤਿਮ ਅਰਦਾਸ ਜਾਂ ਸਸਕਾਰ ਦੀ ਜਾਣਕਾਰੀ' : 'Antim Ardas or Cremation details'}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleTributeSubmit} className="space-y-4 mt-4">
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input type="radio" name="type" value="antim_ardas" checked={newTribute.type === 'antim_ardas'} onChange={(e) => setNewTribute({...newTribute, type: e.target.value})} />
                              <span className="text-sm">{language === 'pa' ? 'ਅੰਤਿਮ ਅਰਦਾਸ' : 'Antim Ardas'}</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input type="radio" name="type" value="saskar" checked={newTribute.type === 'saskar'} onChange={(e) => setNewTribute({...newTribute, type: e.target.value})} />
                              <span className="text-sm">{language === 'pa' ? 'ਸਸਕਾਰ' : 'Cremation'}</span>
                            </label>
                          </div>
                          <Input placeholder={language === 'pa' ? 'ਨਾਮ (ਅੰਗਰੇਜ਼ੀ) *' : 'Name (English) *'} value={newTribute.name} onChange={(e) => setNewTribute({...newTribute, name: e.target.value})} />
                          <Input placeholder={language === 'pa' ? 'ਨਾਮ (ਪੰਜਾਬੀ)' : 'Name (Punjabi)'} value={newTribute.name_pa} onChange={(e) => setNewTribute({...newTribute, name_pa: e.target.value})} />
                          <Input placeholder={language === 'pa' ? 'ਰਿਸ਼ਤਾ (ਜਿਵੇਂ: ਪੁੱਤਰ, ਪਤਨੀ)' : 'Relation (e.g., Son of, Wife of)'} value={newTribute.relation} onChange={(e) => setNewTribute({...newTribute, relation: e.target.value})} />
                          <Input type="date" placeholder={language === 'pa' ? 'ਤਾਰੀਖ਼ *' : 'Date *'} value={newTribute.event_date} onChange={(e) => setNewTribute({...newTribute, event_date: e.target.value})} />
                          
                          {/* Image Upload */}
                          <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                            {imagePreview ? (
                              <div className="relative inline-block">
                                <img src={imagePreview} alt="Preview" className="h-24 w-24 object-cover rounded-lg" />
                                <button type="button" onClick={() => { setImagePreview(null); setNewTribute({...newTribute, image_url: ''}); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <label className="cursor-pointer">
                                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">{language === 'pa' ? 'ਤਸਵੀਰ ਅੱਪਲੋਡ ਕਰੋ' : 'Upload Photo'}</p>
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                              </label>
                            )}
                          </div>
                          
                          <Textarea placeholder={language === 'pa' ? 'ਸੁਨੇਹਾ (ਵਿਕਲਪਿਕ)' : 'Message (optional)'} value={newTribute.message} onChange={(e) => setNewTribute({...newTribute, message: e.target.value})} rows={2} />
                          <Input placeholder={language === 'pa' ? 'ਤੁਹਾਡਾ ਨਾਮ *' : 'Your Name *'} value={newTribute.submitted_by} onChange={(e) => setNewTribute({...newTribute, submitted_by: e.target.value})} />
                          <Input placeholder={language === 'pa' ? 'ਤੁਹਾਡਾ ਫ਼ੋਨ *' : 'Your Phone *'} value={newTribute.submitted_by_phone} onChange={(e) => setNewTribute({...newTribute, submitted_by_phone: e.target.value})} />
                          
                          <div className="flex gap-3 pt-2">
                            <Button type="button" variant="outline" className="flex-1" onClick={() => setShradhanjaliOpen(false)}>{language === 'pa' ? 'ਰੱਦ ਕਰੋ' : 'Cancel'}</Button>
                            <Button type="submit" className="flex-1 bg-gray-800 hover:bg-gray-900" disabled={submitting}>
                              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'pa' ? 'ਜਮ੍ਹਾਂ ਕਰੋ' : 'Submit')}
                            </Button>
                          </div>
                        </form>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Shradhanjali Cards */}
              {tributes.shradhanjali && tributes.shradhanjali.length > 0 ? (
                <div className="space-y-4">
                  {tributes.shradhanjali.map((tribute) => (
                    <Card key={tribute.id} className="border-0 shadow-lg bg-gradient-to-r from-gray-50 to-gray-100">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {tribute.image_url && (
                            <img src={tribute.image_url} alt={tribute.name} className="w-20 h-24 object-cover rounded-lg border-2 border-gray-300" />
                          )}
                          <div className="flex-1">
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-white">
                              {tribute.type === 'antim_ardas' ? (language === 'pa' ? 'ਅੰਤਿਮ ਅਰਦਾਸ' : 'Antim Ardas') : (language === 'pa' ? 'ਸਸਕਾਰ' : 'Cremation')}
                            </span>
                            <h3 className="font-bold text-gray-900 mt-1">
                              {language === 'pa' ? (tribute.name_pa || tribute.name) : tribute.name}
                            </h3>
                            {tribute.relation && (
                              <p className="text-sm text-gray-600">{tribute.relation}</p>
                            )}
                            <p className="text-sm text-amber-700 font-medium mt-1">
                              {new Date(tribute.event_date).toLocaleDateString(language === 'pa' ? 'pa-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                            {tribute.message && (
                              <p className="text-xs text-gray-500 mt-2 italic">"{tribute.message}"</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Flower2 className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">{language === 'pa' ? 'ਕੋਈ ਸ਼ਰਧਾਂਜਲੀ ਨਹੀਂ' : 'No shradhanjali'}</p>
                </div>
              )}
            </div>

            {/* Birthday & Anniversary Wishes */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                    <Cake className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {language === 'pa' ? 'ਮੁਬਾਰਕਾਂ' : 'Wishes'}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {language === 'pa' ? 'ਜਨਮਦਿਨ ਅਤੇ ਵਿਆਹ ਦੀ ਵਰ੍ਹੇਗੰਢ' : 'Birthday & Anniversary'}
                    </p>
                  </div>
                </div>
                <Dialog open={wishesOpen} onOpenChange={setWishesOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-pink-500 hover:bg-pink-600">
                      <Plus className="h-4 w-4 mr-1" />
                      {language === 'pa' ? 'ਸ਼ਾਮਲ ਕਰੋ' : 'Add'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    {submitted ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                          <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {language === 'pa' ? 'ਸਫਲਤਾਪੂਰਵਕ ਜਮ੍ਹਾਂ!' : 'Submitted Successfully!'}
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {language === 'pa' ? 'ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਦਿਖਾਈ ਦੇਵੇਗਾ' : 'Will appear after admin approval'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <DialogHeader>
                          <DialogTitle>{language === 'pa' ? 'ਮੁਬਾਰਕਾਂ ਸ਼ਾਮਲ ਕਰੋ' : 'Add Wishes'}</DialogTitle>
                          <DialogDescription>
                            {language === 'pa' ? 'ਜਨਮਦਿਨ ਜਾਂ ਵਿਆਹ ਦੀ ਵਰ੍ਹੇਗੰਢ' : 'Birthday or Anniversary wishes'}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleTributeSubmit} className="space-y-4 mt-4">
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input type="radio" name="type2" value="birthday" checked={newTribute.type === 'birthday'} onChange={(e) => setNewTribute({...newTribute, type: e.target.value})} />
                              <Cake className="h-4 w-4 text-pink-500" />
                              <span className="text-sm">{language === 'pa' ? 'ਜਨਮਦਿਨ' : 'Birthday'}</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input type="radio" name="type2" value="anniversary" checked={newTribute.type === 'anniversary'} onChange={(e) => setNewTribute({...newTribute, type: e.target.value})} />
                              <Heart className="h-4 w-4 text-red-500" />
                              <span className="text-sm">{language === 'pa' ? 'ਵਿਆਹ ਦੀ ਵਰ੍ਹੇਗੰਢ' : 'Anniversary'}</span>
                            </label>
                          </div>
                          <Input placeholder={language === 'pa' ? 'ਨਾਮ (ਅੰਗਰੇਜ਼ੀ) *' : 'Name (English) *'} value={newTribute.name} onChange={(e) => setNewTribute({...newTribute, name: e.target.value})} />
                          <Input placeholder={language === 'pa' ? 'ਨਾਮ (ਪੰਜਾਬੀ)' : 'Name (Punjabi)'} value={newTribute.name_pa} onChange={(e) => setNewTribute({...newTribute, name_pa: e.target.value})} />
                          <Input type="date" placeholder={language === 'pa' ? 'ਤਾਰੀਖ਼ *' : 'Date *'} value={newTribute.event_date} onChange={(e) => setNewTribute({...newTribute, event_date: e.target.value})} />
                          
                          {/* Image Upload */}
                          <div className="border-2 border-dashed border-pink-200 rounded-lg p-4 text-center">
                            {imagePreview ? (
                              <div className="relative inline-block">
                                <img src={imagePreview} alt="Preview" className="h-24 w-24 object-cover rounded-lg" />
                                <button type="button" onClick={() => { setImagePreview(null); setNewTribute({...newTribute, image_url: ''}); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <label className="cursor-pointer">
                                <Upload className="h-8 w-8 mx-auto text-pink-300 mb-2" />
                                <p className="text-sm text-gray-500">{language === 'pa' ? 'ਤਸਵੀਰ ਅੱਪਲੋਡ ਕਰੋ' : 'Upload Photo'}</p>
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                              </label>
                            )}
                          </div>
                          
                          <Textarea placeholder={language === 'pa' ? 'ਸ਼ੁਭਕਾਮਨਾ ਸੁਨੇਹਾ' : 'Wishes message'} value={newTribute.message} onChange={(e) => setNewTribute({...newTribute, message: e.target.value})} rows={2} />
                          <Input placeholder={language === 'pa' ? 'ਤੁਹਾਡਾ ਨਾਮ *' : 'Your Name *'} value={newTribute.submitted_by} onChange={(e) => setNewTribute({...newTribute, submitted_by: e.target.value})} />
                          <Input placeholder={language === 'pa' ? 'ਤੁਹਾਡਾ ਫ਼ੋਨ *' : 'Your Phone *'} value={newTribute.submitted_by_phone} onChange={(e) => setNewTribute({...newTribute, submitted_by_phone: e.target.value})} />
                          
                          <div className="flex gap-3 pt-2">
                            <Button type="button" variant="outline" className="flex-1" onClick={() => setWishesOpen(false)}>{language === 'pa' ? 'ਰੱਦ ਕਰੋ' : 'Cancel'}</Button>
                            <Button type="submit" className="flex-1 bg-pink-500 hover:bg-pink-600" disabled={submitting}>
                              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'pa' ? 'ਜਮ੍ਹਾਂ ਕਰੋ' : 'Submit')}
                            </Button>
                          </div>
                        </form>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Wishes Cards */}
              {tributes.wishes && tributes.wishes.length > 0 ? (
                <div className="space-y-4">
                  {tributes.wishes.map((tribute) => (
                    <Card key={tribute.id} className={`border-0 shadow-lg ${tribute.type === 'birthday' ? 'bg-gradient-to-r from-pink-50 to-rose-50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {tribute.image_url && (
                            <img src={tribute.image_url} alt={tribute.name} className="w-20 h-20 object-cover rounded-full border-4 border-white shadow-md" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {tribute.type === 'birthday' ? (
                                <Cake className="h-5 w-5 text-pink-500" />
                              ) : (
                                <Heart className="h-5 w-5 text-red-500" />
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${tribute.type === 'birthday' ? 'bg-pink-100 text-pink-600' : 'bg-red-100 text-red-600'}`}>
                                {tribute.type === 'birthday' ? (language === 'pa' ? 'ਜਨਮਦਿਨ' : 'Birthday') : (language === 'pa' ? 'ਵਿਆਹ ਦੀ ਵਰ੍ਹੇਗੰਢ' : 'Anniversary')}
                              </span>
                            </div>
                            <h3 className="font-bold text-gray-900 mt-1 text-lg">
                              {language === 'pa' ? (tribute.name_pa || tribute.name) : tribute.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {new Date(tribute.event_date).toLocaleDateString(language === 'pa' ? 'pa-IN' : 'en-IN', { day: 'numeric', month: 'long' })}
                            </p>
                            {tribute.message && (
                              <p className="text-sm text-gray-700 mt-2 bg-white/50 p-2 rounded">🎉 {tribute.message}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-pink-50 rounded-xl">
                  <Gift className="h-10 w-10 mx-auto text-pink-200 mb-2" />
                  <p className="text-sm text-gray-400">{language === 'pa' ? 'ਕੋਈ ਮੁਬਾਰਕਾਂ ਨਹੀਂ' : 'No wishes today'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 -mt-6 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md overflow-hidden">
                <CardContent className="p-5 md:p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Heroes Section */}
      <section className="py-16 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="h-4 w-4" />
              {t('heroes')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {language === 'pa' ? 'ਸਾਡੇ ਮਾਣਮੱਤੇ ਨਾਇਕ' : 'Our Proud Heroes'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {heroes.map((hero) => (
              <Card key={hero.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-2/5 relative overflow-hidden">
                      <div 
                        className="aspect-[3/4] md:aspect-auto md:h-full cursor-pointer relative"
                        onClick={() => setSelectedHero(hero)}
                      >
                        <img 
                          src={hero.image} 
                          alt={getText(hero.name)}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                            <ZoomIn className="h-6 w-6 text-amber-600" />
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:bg-gradient-to-r pointer-events-none"></div>
                      <div className="absolute bottom-4 left-4 md:hidden">
                        <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {hero.year}
                        </span>
                      </div>
                    </div>
                    <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                      <span className="hidden md:inline-block bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full w-fit mb-3">
                        {hero.year}
                      </span>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                        {getText(hero.name)}
                      </h3>
                      <p className="text-amber-600 font-medium text-sm mb-4">
                        {getText(hero.title)}
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {getText(hero.description)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Hero Image Lightbox */}
          <Dialog open={!!selectedHero} onOpenChange={() => setSelectedHero(null)}>
            <DialogContent className="max-w-4xl bg-black/95 border-0 p-0">
              <div className="relative">
                <button 
                  onClick={() => setSelectedHero(null)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="h-6 w-6 text-white" />
                </button>
                
                {selectedHero && (
                  <div className="flex flex-col items-center p-4">
                    <img 
                      src={selectedHero.image} 
                      alt={getText(selectedHero.name)}
                      className="max-h-[70vh] w-auto object-contain rounded-lg"
                    />
                    <div className="text-center mt-4">
                      <h3 className="text-xl font-bold text-white">
                        {getText(selectedHero.name)}
                      </h3>
                      <p className="text-amber-400 text-sm mt-1">
                        {getText(selectedHero.title)}
                      </p>
                      <span className="inline-block bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full mt-2">
                        {selectedHero.year}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Pattis Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'pa' ? 'ਮਰਹਾਣਾ ਦੇ ਮੁਹੱਲੇ' : 'Mohallas of Marhana'}
            </h2>
            <p className="text-gray-500">
              {language === 'pa' ? '5 ਪੱਟੀਆਂ ਅਤੇ 2 ਹੋਰ ਇਲਾਕੇ' : '5 Pattis and 2 other areas'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {pattis.map((patti, index) => (
              <Link to={`/directory?patti=${patti.id}`} key={patti.id}>
                <Card className="group hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-amber-100 cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-4 group-hover:from-amber-200 group-hover:to-orange-200 transition-colors">
                      <span className="text-2xl font-bold text-amber-600">{index + 1}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">
                      {language === 'pa' ? patti.name_pa : patti.name_en}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/directory">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                {t('viewAll')} {t('directory')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* News & Events Section */}
      <section className="py-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Latest News */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Newspaper className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('latestNews')}</h2>
                </div>
                <Link to="/news" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
                  {t('viewAll')} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="space-y-4">
                {newsItems.map((news) => (
                  <Card key={news.id} className="group hover:shadow-md transition-all duration-300 border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center ${
                          news.type === 'announcement' ? 'bg-blue-100 text-blue-600' :
                          news.type === 'news' ? 'bg-emerald-100 text-emerald-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          <Newspaper className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-400 mb-1">{new Date(news.date).toLocaleDateString()}</p>
                          <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">
                            {getNewsText(news, 'title')}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {getNewsText(news, 'content')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Upcoming Events */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('upcomingEvents')}</h2>
                </div>
                <Link to="/events" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
                  {t('viewAll')} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="space-y-4">
                {eventsData.map((event) => (
                  <Card key={event.id} className="group hover:shadow-md transition-all duration-300 border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-16 text-center">
                          <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-t-lg py-1 text-xs font-medium">
                            {new Date(event.date).toLocaleDateString('en', { month: 'short' })}
                          </div>
                          <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg py-2">
                            <span className="text-2xl font-bold text-gray-900">
                              {new Date(event.date).getDate()}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">
                            {getEventText(event, 'title')}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {event.time} • {getEventText(event, 'location')}
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            {getEventText(event, 'description')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {language === 'pa' ? 'ਪਿੰਡ ਨਾਲ ਜੁੜੇ ਰਹੋ' : 'Stay Connected with Village'}
          </h2>
          <p className="text-amber-100 mb-8 max-w-2xl mx-auto">
            {language === 'pa' 
              ? 'ਪਿੰਡ ਦੀਆਂ ਖ਼ਬਰਾਂ, ਸਮਾਗਮਾਂ ਅਤੇ ਸੇਵਾਵਾਂ ਬਾਰੇ ਜਾਣੋ' 
              : 'Access village directory, submit grievances, and stay updated with news and events'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/directory">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50 shadow-lg px-8">
                {t('directory')}
              </Button>
            </Link>
            <Link to="/grievance">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                {t('submitGrievance')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
